import { isLocal } from "@configs/env";
import { BrowserClient, Scope, defaultStackParser, getDefaultIntegrations, makeFetchTransport } from "@sentry/browser";
import logger from "@utils/logger";

/**
 * Custom Sentry client for manual reporting
 * Events are only sent via captureMessage
 * https://docs.sentry.io/platforms/javascript/best-practices/multiple-sentry-instances
 */
export class SentryManualInstance {
  private client: BrowserClient | null = null;
  private scope: Scope | null = null;

  constructor() {
    // Filter out integrations that use global state to avoid conflicts
    const integrations = getDefaultIntegrations({}).filter((defaultIntegration) => {
      return !["BrowserApiErrors", "Breadcrumbs", "GlobalHandlers"].includes(defaultIntegration.name);
    });

    const sentryDsn = import.meta.env.VITE_SENTRY_MANUAL_DSN;
    const version = import.meta.env.VITE_APP_VERSION;

    // Only initialize in production or staging
    if (!sentryDsn || isLocal()) {
      return;
    }

    // Custom client with sample rate 100%
    this.client = new BrowserClient({
      dsn: sentryDsn,
      environment: import.meta.env.VITE_BRAND_KEY,
      transport: makeFetchTransport,
      stackParser: defaultStackParser,
      release: version,
      integrations: integrations,
      // only accept events that are explicitly captured via captureMessage
      beforeSend: (event) => {
        if (event.message) {
          return event;
        }
        return null;
      },
      sampleRate: 1.0,
      autoSessionTracking: false,
      attachStacktrace: false,
    });

    this.scope = new Scope();
    this.scope.setClient(this.client);
    this.init();
  }

  /**
   * Initialize the Sentry client
   */
  private init(): void {
    try {
      this.client?.init();
      logger._console.log("Sentry Manual client initialized");
    } catch (error) {
      logger._console.log("Sentry Manual client initialization failed:", error);
    }
  }

  public captureMessage(
    message: string,
    level: "info" | "warning" | "error" = "info",
    extra?: Record<string, any>,
  ): string | undefined {
    try {
      if (extra) {
        this.scope?.setExtra("extra_data", extra);
      }
      return this.scope?.captureMessage(message, level);
    } catch (error) {
      logger._console.log("Sentry Manual client captureMessage failed:", error);
    }
  }

  public captureException(exception: unknown): string | undefined {
    try {
      return this.scope?.captureException(exception);
    } catch (error) {
      logger._console.log("Sentry Manual client captureException failed:", error);
    }
  }

  public setTag(key: string, value: string): void {
    try {
      this.scope?.setTag(key, value);
    } catch (error) {
      logger._console.log("Sentry Manual client setTag failed:", error);
    }
  }
}

// Create and export a singleton instance
export const sentryManual = new SentryManualInstance();

export const initSentryManual = () => {
  return sentryManual;
};
